import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listAllMailboxes, adminCreateMailbox, setMailboxQuota, updateMailboxPassword,
  updateMailboxPasswordByEmail, restrictMailbox, unrestrictMailbox,
  syncMailboxUsage, deleteMailboxAdmin, deleteMailboxByEmail,
  AdminMailbox,
} from '../services/mailService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';

import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { formatLocal } from '../utils/dateHelper';

const FUNC = 'ee_mail_accounts';

const btnBase: React.CSSProperties = { padding: '4px 10px', border: '1px solid', borderRadius: 4, cursor: 'pointer', fontSize: 12, background: 'none' };

const EEMailAccountsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError, showError } = useMessage();

  const hasInit = useRef(false);

  const [mailboxes, setMailboxes] = useState<AdminMailbox[]>([]);
  const [loading, setLoading] = useState(false);

  // create form
  const [showCreate, setShowCreate] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPwd, setCreatePwd] = useState('');
  const [creating, setCreating] = useState(false);

  // inline edits
  const [quotaEdit, setQuotaEdit] = useState<{ key: string; value: string } | null>(null);
  const [pwdEdit, setPwdEdit] = useState<{ key: string; value: string } | null>(null);

  // loading states keyed by email_address
  const [syncing, setSyncing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [locking, setLocking] = useState<string | null>(null);
  const [savingPwd, setSavingPwd] = useState<string | null>(null);

  const canCreate = hasPermission(FUNC, 'create');
  const canUpdate = hasPermission(FUNC, 'update');
  const canDelete = hasPermission(FUNC, 'delete');

  const load = async () => {
    setLoading(true);
    try {
      setMailboxes(await listAllMailboxes());
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      validateSession(FUNC).then(v => { if (v) load(); });
    }
  }, [permLoading]);

  const handleCreate = async () => {
    if (!createEmail.trim() || !createPwd) {
      showError('ERR020006', { field: t('common.required'), detail: '' });
      return;
    }
    setCreating(true);
    try {
      await adminCreateMailbox(createEmail.trim().toLowerCase(), createPwd);
      showSuccess('SYS020001', { name: createEmail.trim() });
      setCreateEmail(''); setCreatePwd(''); setShowCreate(false);
      await load();
    } catch (err) { showApiError(err); }
    finally { setCreating(false); }
  };

  const handleSaveQuota = async (mb: AdminMailbox) => {
    if (!quotaEdit) return;
    try {
      const updated = await setMailboxQuota(mb.id!, quotaEdit.value.trim() || null);
      setMailboxes(ms => ms.map(m => m.email_address === mb.email_address ? updated : m));
      showSuccess('SYS020001', { name: t('mail.accounts.quota') });
      setQuotaEdit(null);
    } catch (err) { showApiError(err); }
  };

  const handleSavePwd = async (mb: AdminMailbox) => {
    if (!pwdEdit?.value) return;
    setSavingPwd(mb.email_address);
    try {
      if (mb.in_db && mb.id) {
        await updateMailboxPassword(mb.id, pwdEdit.value);
      } else {
        await updateMailboxPasswordByEmail(mb.email_address, pwdEdit.value);
      }
      showSuccess('SYS020001', { name: mb.email_address });
      setPwdEdit(null);
    } catch (err) { showApiError(err); }
    finally { setSavingPwd(null); }
  };

  const handleToggleLock = async (mb: AdminMailbox) => {
    if (!mb.id) return;
    setLocking(mb.email_address);
    try {
      if (mb.is_active) {
        await restrictMailbox(mb.id);
        setMailboxes(ms => ms.map(m => m.email_address === mb.email_address ? { ...m, is_active: false } : m));
      } else {
        await unrestrictMailbox(mb.id);
        setMailboxes(ms => ms.map(m => m.email_address === mb.email_address ? { ...m, is_active: true } : m));
      }
    } catch (err) { showApiError(err); }
    finally { setLocking(null); }
  };

  const handleSyncUsage = async (mb: AdminMailbox) => {
    if (!mb.id) return;
    setSyncing(mb.email_address);
    try {
      const updated = await syncMailboxUsage(mb.id);
      setMailboxes(ms => ms.map(m => m.email_address === mb.email_address ? updated : m));
    } catch (err) { showApiError(err); }
    finally { setSyncing(null); }
  };

  const handleDelete = async (mb: AdminMailbox) => {
    if (!window.confirm(t('common.confirmDelete', { name: mb.email_address }))) return;
    setDeleting(mb.email_address);
    try {
      if (mb.in_db && mb.id) await deleteMailboxAdmin(mb.id);
      else await deleteMailboxByEmail(mb.email_address);
      setMailboxes(ms => ms.filter(m => m.email_address !== mb.email_address));
      showSuccess('SYS020001', { name: mb.email_address });
    } catch (err) { showApiError(err); }
    finally { setDeleting(null); }
  };

  const thSt: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f5f5f5', borderBottom: '1px solid #ddd', whiteSpace: 'nowrap' };
  const tdSt: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };
  const inSt: React.CSSProperties = { padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 };

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: '12px 24px' }}>
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          {canCreate && (
            <button onClick={() => setShowCreate(s => !s)}
              style={{ padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              + {t('mail.accounts.createMailbox')}
            </button>
          )}
          <button onClick={load} style={{ padding: '6px 14px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
            ↺ {t('common.refresh')}
          </button>
        </div>

        {showCreate && (
          <div style={{ background: '#fafafa', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 16, maxWidth: 480 }}>
            <h4 style={{ margin: '0 0 12px' }}>{t('mail.accounts.createMailbox')}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input value={createEmail} onChange={e => setCreateEmail(e.target.value)}
                placeholder="user@domain.com" style={{ ...inSt, fontSize: 14, padding: '8px 10px' }} />
              <input type="password" value={createPwd} onChange={e => setCreatePwd(e.target.value)}
                placeholder={t('mail.myMailbox.passwordHint')} style={{ ...inSt, fontSize: 14, padding: '8px 10px' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleCreate} disabled={creating}
                  style={{ padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  {creating ? t('common.loading') : t('common.confirm')}
                </button>
                <button onClick={() => setShowCreate(false)}
                  style={{ padding: '8px 14px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={thSt}>{t('mail.accounts.email')}</th>
                  <th style={thSt}>{t('mail.accounts.orgId')}</th>
                  <th style={thSt}>{t('mail.accounts.quota')}</th>
                  <th style={thSt}>{t('mail.accounts.usage')}</th>
                  <th style={thSt}>{t('mail.accounts.pct')}</th>
                  <th style={thSt}>{t('mail.accounts.syncedAt')}</th>
                  <th style={thSt}>{t('common.status')}</th>
                  <th style={thSt}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {mailboxes.map(mb => {
                  const key = mb.email_address;
                  return (
                    <tr key={key} style={!mb.in_db ? { background: '#fffde7' } : undefined}>
                      <td style={tdSt}>
                        <strong>{mb.email_address}</strong>
                        {!mb.in_db && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#f57c00', background: '#fff3e0', padding: '1px 5px', borderRadius: 3 }}>
                            主機帳號
                          </span>
                        )}
                        {mb.user_id && <div style={{ fontSize: 12, color: '#888' }}>UID: {mb.user_id}</div>}
                      </td>
                      <td style={tdSt}>{mb.org_id ?? '-'}</td>
                      <td style={tdSt}>
                        {mb.in_db && mb.id ? (
                          quotaEdit?.key === key ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <input value={quotaEdit.value} onChange={e => setQuotaEdit({ key, value: e.target.value })}
                                placeholder="e.g. 500M" style={{ ...inSt, width: 80 }} />
                              <button onClick={() => handleSaveQuota(mb)}
                                style={{ ...btnBase, borderColor: '#1976d2', color: '#1976d2' }}>OK</button>
                              <button onClick={() => setQuotaEdit(null)}
                                style={{ ...btnBase, borderColor: '#aaa', color: '#666' }}>✗</button>
                            </div>
                          ) : (
                            <span onClick={() => canUpdate && setQuotaEdit({ key, value: mb.quota ?? '' })}
                              style={{ cursor: canUpdate ? 'pointer' : 'default', textDecoration: canUpdate ? 'underline dotted' : 'none' }}>
                              {mb.quota ?? t('common.unlimited')}
                            </span>
                          )
                        ) : '-'}
                      </td>
                      <td style={tdSt}>{mb.quota_used ?? '-'}</td>
                      <td style={tdSt}>
                        {mb.quota_pct != null ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ background: '#e0e0e0', borderRadius: 4, width: 60, height: 8, overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(mb.quota_pct, 100)}%`, height: '100%',
                                background: mb.quota_pct > 90 ? '#d32f2f' : mb.quota_pct > 70 ? '#f57c00' : '#388e3c',
                              }} />
                            </div>
                            <span style={{ fontSize: 12 }}>{mb.quota_pct}%</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={{ ...tdSt, fontSize: 12, color: '#888' }}>
                        {mb.quota_synced_at ? formatLocal(mb.quota_synced_at) : '-'}
                      </td>
                      <td style={tdSt}>
                        <span style={{ color: mb.is_active ? '#388e3c' : '#d32f2f', fontSize: 13 }}>
                          {mb.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td style={tdSt}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {canUpdate && (
                            pwdEdit?.key === key ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input type="password" value={pwdEdit.value}
                                  onChange={e => setPwdEdit({ key, value: e.target.value })}
                                  placeholder={t('mail.accounts.newPassword')}
                                  style={{ ...inSt, width: 120 }} />
                                <button onClick={() => handleSavePwd(mb)} disabled={savingPwd === key}
                                  style={{ ...btnBase, borderColor: '#1976d2', color: '#1976d2' }}>
                                  {savingPwd === key ? '...' : 'OK'}
                                </button>
                                <button onClick={() => setPwdEdit(null)}
                                  style={{ ...btnBase, borderColor: '#aaa', color: '#666' }}>✗</button>
                              </div>
                            ) : (
                              <button onClick={() => setPwdEdit({ key, value: '' })}
                                style={{ ...btnBase, borderColor: '#757575', color: '#757575' }}>
                                {t('mail.accounts.changePassword')}
                              </button>
                            )
                          )}
                          {canUpdate && mb.in_db && mb.id && (
                            <button onClick={() => handleToggleLock(mb)} disabled={locking === key}
                              style={{ ...btnBase, borderColor: mb.is_active ? '#f57c00' : '#388e3c', color: mb.is_active ? '#f57c00' : '#388e3c' }}>
                              {locking === key ? '...' : mb.is_active ? t('mail.accounts.lock') : t('mail.accounts.unlock')}
                            </button>
                          )}
                          {canUpdate && mb.in_db && mb.id && (
                            <button onClick={() => handleSyncUsage(mb)} disabled={syncing === key}
                              style={{ ...btnBase, borderColor: '#388e3c', color: '#388e3c' }}>
                              {syncing === key ? '...' : t('mail.accounts.syncUsage')}
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDelete(mb)} disabled={deleting === key}
                              style={{ ...btnBase, borderColor: '#d32f2f', color: '#d32f2f' }}>
                              {deleting === key ? '...' : t('common.delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {mailboxes.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ ...tdSt, textAlign: 'center', color: '#999' }}>
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EEMailAccountsPage;